import PropTypes from 'prop-types';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useSnackbar } from 'notistack';
// @mui
import { useTheme } from '@mui/material/styles';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
// hooks
import useAuth from 'src/hooks/useAuth';
import useSettings from 'src/hooks/useSettings';
// components
import MyAvatar from 'src/components/MyAvatar';
import Icon from 'src/components/universe/Icon';
import { segmentSx, segmentedTrackSx } from 'src/components/universe/Surface';
import {
  RADIUS,
  UNIVERSE_FONT,
  accentOf,
  accentTint,
  focusRingSx,
  hairlineOf,
  touchHitSx
} from 'src/theme/universe/brand';
import { toneInkOf } from 'src/theme/universe/tones';
//
import { ROLE_WORD, accountLinksFor } from './vendorNav';

// ---------------------------------------------------------------------------
// THE ACCOUNT, in one panel: who is signed in, the account's own pages (a
// Linguist's profile and password), the theme, and signing out. The rail's
// account button opens it as a menu; on a phone it is the foot of the More
// sheet. One component, so the two can never offer different things.
//
// The old menu held the name, the email and Logout, and the theme lived in
// the template's settings drawer behind a gear in the header. The theme is
// the one setting a vendor changes, so it is here, as two segments.
// ---------------------------------------------------------------------------

/** The display name the session carries, whichever shape it has. */
export function accountName(user) {
  return (
    user?.name ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.email ||
    ''
  );
}

/** Sign out and land on the vendor sign-in, as every vendor type always did. */
export function useSignOut(onDone) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  return async () => {
    try {
      await logout();
      navigate('/vendors');
      onDone?.();
    } catch (error) {
      enqueueSnackbar('You could not be signed out. Please try again.', {
        variant: 'error'
      });
    }
  };
}

const rowSx = (theme) => ({
  width: 1,
  minHeight: 40,
  px: 1.25,
  gap: 1.25,
  justifyContent: 'flex-start',
  borderRadius: `${RADIUS.control}px`,
  fontFamily: UNIVERSE_FONT,
  fontSize: 14,
  fontWeight: 500,
  color: 'text.primary',
  textDecoration: 'none',
  '& svg': { color: 'text.secondary' },
  '&:hover': {
    bgcolor: accentTint(theme, 'hover'),
    color: accentOf(theme),
    '& svg': { color: accentOf(theme) }
  },
  '&:focus-visible': focusRingSx(theme),
  '@media (pointer: coarse)': { minHeight: 44 }
});

VendorAccountPanel.propTypes = {
  /** Called after a link is followed or the session ends (closes the layer). */
  onClose: PropTypes.func,
  /** The identity line at the top; the More sheet already has a title. */
  showIdentity: PropTypes.bool
};

export default function VendorAccountPanel({ onClose, showIdentity = true }) {
  const theme = useTheme();
  const { user, type } = useAuth();
  const { themeMode, onChangeMode } = useSettings();
  const signOut = useSignOut(onClose);
  const links = accountLinksFor(type);
  const name = accountName(user);

  const setMode = (mode) => {
    if (mode !== themeMode) onChangeMode({ target: { value: mode } });
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      {showIdentity && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ px: 1.25, pt: 0.5, pb: 1.5 }}
        >
          <MyAvatar sx={{ width: 40, height: 40, flex: 'none' }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              noWrap
              sx={{
                fontFamily: UNIVERSE_FONT,
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.35,
                color: 'text.primary'
              }}
            >
              {name}
            </Typography>
            <Typography
              noWrap
              sx={{
                fontFamily: UNIVERSE_FONT,
                fontSize: 12,
                fontWeight: 500,
                lineHeight: 1.4,
                color: 'text.secondary'
              }}
            >
              {[ROLE_WORD[type], user?.email].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
        </Stack>
      )}

      {links.length > 0 && (
        <Stack
          spacing={0.25}
          sx={{
            py: 0.75,
            borderTop: showIdentity ? `1px solid ${hairlineOf(theme)}` : 0
          }}
        >
          {links.map((link) => (
            <ButtonBase
              key={link.key}
              component={RouterLink}
              to={link.path}
              onClick={onClose}
              sx={rowSx(theme)}
            >
              <Icon name={link.icon} size="md" />
              {link.label}
            </ButtonBase>
          ))}
        </Stack>
      )}

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.5}
        sx={{
          px: 1.25,
          py: 1.25,
          borderTop: `1px solid ${hairlineOf(theme)}`
        }}
      >
        <Typography
          id="vendor-theme-label"
          sx={{
            fontFamily: UNIVERSE_FONT,
            fontSize: 14,
            fontWeight: 500,
            color: 'text.primary'
          }}
        >
          Theme
        </Typography>
        <Box
          role="radiogroup"
          aria-labelledby="vendor-theme-label"
          sx={segmentedTrackSx(theme)}
        >
          {[
            { mode: 'light', label: 'Light', icon: 'Sun1' },
            { mode: 'dark', label: 'Dark', icon: 'Moon' }
          ].map((option) => {
            const on = themeMode === option.mode;
            return (
              <Box
                key={option.mode}
                component="button"
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setMode(option.mode)}
                sx={{
                  ...segmentSx(theme, on),
                  '@media (pointer: coarse)': touchHitSx
                }}
              >
                <Icon name={option.icon} size="xs" variant={on ? 'Bold' : 'Linear'} />
                {option.label}
              </Box>
            );
          })}
        </Box>
      </Stack>

      <Box sx={{ pt: 0.75, borderTop: `1px solid ${hairlineOf(theme)}` }}>
        <ButtonBase
          onClick={signOut}
          sx={{
            ...rowSx(theme),
            '&&': { color: toneInkOf(theme, 'error') },
            '&& svg': { color: toneInkOf(theme, 'error') }
          }}
        >
          <Icon name="Logout" size="md" />
          Sign out
        </ButtonBase>
      </Box>
    </Box>
  );
}
